/* eslint-disable max-classes-per-file */
import React, { ReactNode } from 'react';
import { inject, observer } from 'mobx-react';
import { IUserStore } from '@stores/user';
import { RouteComponentProps } from 'react-router-dom';
import { Path } from '@components/routes/constants';

interface IAuthenticateProps extends RouteComponentProps {
  userStore: IUserStore;
  children?: ReactNode;
}

type ComposedComponent = (props: IAuthenticateProps) => JSX.Element | null;

/** Paths a user suspended in their selected organization is still allowed to visit. */
const SUSPENDED_USER_ALLOWED_PATHS: string[] = [Path.USER_SUSPENDED, Path.SELECT_ORGANIZATION];

abstract class AuthRouteGuard extends React.Component<IAuthenticateProps> {
  abstract render(): JSX.Element;

  protected abstract hasActiveSession(): boolean;

  public componentDidMount() {
    this._checkAndRedirect();
  }

  public componentDidUpdate() {
    this._checkAndRedirect();
  }

  /**
   * The path the current user must be redirected to, or `null` when the current
   * location is allowed. Shared by the redirect effect and the render guard so
   * the two never drift out of sync.
   */
  private getRedirectPath(): string | null {
    const { location, userStore } = this.props;

    if (!this.hasActiveSession()) {
      return Path.LOGIN;
    }

    if (userStore.isUserSuspendedInSelectedOrganization) {
      return SUSPENDED_USER_ALLOWED_PATHS.includes(location.pathname) ? null : Path.USER_SUSPENDED;
    }

    return location.pathname === Path.USER_SUSPENDED ? Path.HOME : null;
  }

  public _checkAndRedirect() {
    const { history, location } = this.props;
    const redirectPath = this.getRedirectPath();

    if (!redirectPath || redirectPath === location.pathname) {
      return;
    }

    if (redirectPath === Path.LOGIN) {
      history.push(redirectPath);
    } else {
      history.replace(redirectPath);
    }
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  protected _render(ComposedComponent: ComposedComponent) {
    return <>{this.getRedirectPath() === null && <ComposedComponent {...this.props} />}</>;
  }
}

/**
 * Route guard that requires User to be in a logged in state, eg have valid user store with idToken
 */
export const requiresAuth = (ComposedComponent: ComposedComponent) => {
  @inject('userStore')
  @observer
  class Authenticate extends AuthRouteGuard {
    protected hasActiveSession(): boolean {
      return this.props.userStore.loggedIn;
    }

    public render() {
      return super._render(ComposedComponent);
    }
  }
  return Authenticate;
};

/**
 * Route guard that requires User to either be logged in or have started an MFA flow,
 * such as MFA submission, or MFA set up
 */
export const requiresMfaSession = (ComposedComponent: ComposedComponent) => {
  @inject('userStore')
  @observer
  class Authenticate extends AuthRouteGuard {
    protected hasActiveSession(): boolean {
      const {
        userStore: { loggedIn, mfaData },
      } = this.props;
      return loggedIn || !!mfaData?.sessionToken;
    }

    public render() {
      return super._render(ComposedComponent);
    }
  }
  return Authenticate;
};
